import { Tab, TabPanel, Tabs, TabsList } from "@mui/base";
import ReactApexChart from "react-apexcharts";
import Skeleton from "react-loading-skeleton";

import {
  KeywordHistoricalRanking,
  KeywordMonthlySearches,
  Keyword as KeywordType,
} from "../../api/Api";
import { formatNumber } from "../../helpers/helpers";
import NoData from "../containers/NoData";
import Card from "../ui/card/Card";
import Spinner from "../ui/spinner/Spinner";
import styles from "./Keyword.module.scss";

type KeywordProps = {
  data: KeywordType;
  isLoading: boolean;
  keywordHistoricalRanking: KeywordHistoricalRanking | null;
  keywordSearchVolume: KeywordMonthlySearches | null;
  isLoadingHistoricalRanking: boolean;
  isLoadingKeywordSearchVolume: boolean;
};

const chartData = (
  data: { labels: string[]; values: number[] },
  isReversed?: boolean,
) => {
  return {
    series: [
      {
        name: "Position",
        data: data?.values,
      },
    ],
    options: {
      chart: {
        height: 350,
        type: "line" as const,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "straight" as const,
        width: 2,
        color: ["#047bf8"],
      },
      title: {
        align: "left" as const,
      },
      grid: {
        yaxis: {
          lines: {
            show: true,
          },
        },
        xaxis: {
          lines: {
            show: false,
          },
        },
      },

      xaxis: {
        categories: data?.labels,
      },
      yaxis: {
        reversed: isReversed || false,
      },
      tooltip: {
        y: {
          formatter: function (value: number) {
            return formatNumber(value);
          },
        },
      },
    },
  };
};
const Keyword = ({
  data,
  isLoading,
  keywordHistoricalRanking,
  keywordSearchVolume,
  isLoadingKeywordSearchVolume,
  isLoadingHistoricalRanking,
}: KeywordProps) => {
  const keywordData = [
    {
      label: "Search Volume",
      value: data?.search_vol ? formatNumber(data.search_vol) : "N/A",
    },
    {
      label: "Cost per Click",
      value: data?.cpc ? `$${data?.cpc.toFixed(2)}` : "N/A",
    },
    {
      label: "COMP",
      value: data?.cmp ? Math.ceil(data?.cmp * 100).toFixed(0) : "N/A",
    },
    {
      label: "Traffic",
      value: data?.proj_traffic
        ? formatNumber(Math.ceil(data.proj_traffic))
        : "N/A",
    },
    {
      label: "Keyword Score",
      value: data?.alli_score
        ? formatNumber(Math.ceil(data.alli_score))
        : "N/A",
    },
  ];

  const tabs = [
    {
      id: "week",
      label: "Week",
      content: !keywordHistoricalRanking?.week.labels?.length ? (
        <NoData title="No Week Ranking" />
      ) : (
        <ReactApexChart
          options={chartData(keywordHistoricalRanking?.week, true)?.options}
          series={chartData(keywordHistoricalRanking?.week, true)?.series}
          type="line"
          height={350}
        />
      ),
    },
    {
      id: "month",
      label: "Month",
      data: keywordHistoricalRanking?.month,
      content: !keywordHistoricalRanking?.month.labels?.length ? (
        <NoData title="No Month Ranking" />
      ) : (
        <ReactApexChart
          options={chartData(keywordHistoricalRanking?.month, true)?.options}
          series={chartData(keywordHistoricalRanking?.month, true)?.series}
          type="line"
          height={350}
        />
      ),
    },
    {
      id: "alltime",
      label: "Life",
      data: keywordHistoricalRanking?.alltime,
      content: !keywordHistoricalRanking?.alltime.labels?.length ? (
        <NoData title="No Life Ranking" />
      ) : (
        <ReactApexChart
          options={chartData(keywordHistoricalRanking?.alltime, true)?.options}
          series={chartData(keywordHistoricalRanking?.alltime, true)?.series}
          type="line"
          height={350}
        />
      ),
    },
  ];
  const volumeData = {
    labels:
      keywordSearchVolume
        ?.map((item) => `${item.month}/${item.year?.toString()?.slice(2)}`)
        ?.reverse() || [],
    values: keywordSearchVolume?.map((item) => item.volume)?.reverse() || [],
  };

  return (
    <div className={styles.keywordDetsils}>
      <Card title={`Overview`}>
        <div className={styles.keywordDetsilsList}>
          {isLoadingHistoricalRanking ? (
            <Spinner />
          ) : (
            keywordData?.map((item, index) => (
              <div key={index} className={styles.keywordDetsilsListBlock}>
                <div className={styles.keywordDetsilsListLabel}>
                  {item.label}
                </div>
                {isLoading ? (
                  <div className={styles.keywordDetsilsListValue}>
                    {" "}
                    <Skeleton width={50} />{" "}
                  </div>
                ) : (
                  <div className={styles.keywordDetsilsListValue}>
                    {item.value}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </Card>
      <Card title="Ranking Dynamics">
        <div className={styles.cardSubtitle}>Keyword Rank to Date</div>
        <Tabs defaultValue={tabs[0]?.label}>
          <TabsList className={styles.tabsList}>
            {tabs?.map((tab) => (
              <Tab className={styles.tabButton} value={tab.label}>
                {tab.label}
              </Tab>
            ))}
          </TabsList>
          {tabs?.map((tab) => {
            return (
              <TabPanel value={tab.label} key={tab.id}>
                {tab.content}
              </TabPanel>
            );
          })}
        </Tabs>
      </Card>
      <Card title="Search Volume Dynamics">
        <div className={styles.cardSubtitle}>Number of Searches to Date</div>
        {isLoadingKeywordSearchVolume || !keywordSearchVolume ? (
          <Spinner />
        ) : keywordSearchVolume.length === 0 ? (
          <NoData title="No Search Volume Dynamics" />
        ) : (
          <ReactApexChart
            options={chartData(volumeData).options}
            series={chartData(volumeData).series}
            type="line"
            height={350}
          />
        )}
      </Card>
    </div>
  );
};
export default Keyword;
